import React, { useEffect, useRef, useState } from "react";
import { Card, Button } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";

let ytApiPromise;
const loadYouTubeApi = () => {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (!ytApiPromise) {
    ytApiPromise = new Promise((resolve) => {
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (prev) prev();
        resolve(window.YT);
      };
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      document.head.appendChild(tag);
    });
  }
  return ytApiPromise;
};

// Loads a thumbnail only; the heavy YouTube player loads when the user clicks play.
const YouTubeEmbed = ({ id, title, poster }) => {
  const [playing, setPlaying] = useState(false);
  const frame = {
    position: "relative",
    width: "100%",
    aspectRatio: "16 / 9",
    background: "#000",
    borderRadius: "calc(0.375rem - 1px)",
    overflow: "hidden",
    marginTop: "10px",
    marginBottom: "10px",
  };
  const mountRef = useRef(null);

  useEffect(() => {
    if (!playing) return undefined;
    let player;
    let cancelled = false;
    loadYouTubeApi().then((YT) => {
      if (cancelled || !mountRef.current) return;
      // The captions module is unloaded so the viewer's saved "always show captions" setting is ignored.
      const disableCaptions = () => {
        try {
          player.unloadModule("captions");
          player.unloadModule("cc");
        } catch (e) {
          /* player not ready yet */
        }
      };
      player = new YT.Player(mountRef.current, {
        videoId: id,
        width: "100%",
        height: "100%",
        host: "https://www.youtube-nocookie.com",
        playerVars: { autoplay: 1, rel: 0, cc_load_policy: 0, playsinline: 1 },
        events: {
          onReady: disableCaptions,
          onApiChange: disableCaptions,
          onStateChange: (e) => {
            if (e.data === YT.PlayerState.PLAYING) disableCaptions();
          },
        },
      });
    });
    return () => {
      cancelled = true;
      if (player && player.destroy) player.destroy();
    };
  }, [playing, id]);

  if (playing) {
    return (
      <div style={frame}>
        <div
          ref={mountRef}
          title={title}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
        />
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => setPlaying(true)}
      aria-label={`Play ${title}`}
      style={{ ...frame, display: "block", padding: 0, border: 0, cursor: "pointer" }}
    >
      <img
        src={poster || `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`}
        alt={title}
        loading="lazy"
        onError={(e) => {
          e.currentTarget.onerror = null;
          e.currentTarget.src = `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
        }}
        style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "inherit" }}
      />
      <span
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 80,
          height: 56,
          borderRadius: 14,
          background: "#f25286",
          color: "#fff",
          fontSize: 28,
          lineHeight: "56px",
        }}
      >
        ▶
      </span>
    </button>
  );
};

const PortfolioCard = ({
  image,
  youtubeId,
  poster,
  title,
  descriptions,
  listItems,
  buttons,
  source,
}) => {
  return (
    <Card style={{ marginBottom: "20px" }}>
      <Card.Header>
        {youtubeId ? (
          <YouTubeEmbed id={youtubeId} title={title} poster={poster} />
        ) : (
          <Card.Img
            src={image}
            className="img-fluid"
            style={{
              height: "auto",
              borderRadius: "calc(0.375rem - 1px)",
              justifyContent: "center",
              marginTop: "10px",
              marginBottom: "10px",
            }}
          />
        )}
        {source && (
          <div style={{ marginTop: "10px", color: "#d9d7d7" }}>
            Source:{" "}
            <a
              href={source.link}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#d9d7d7" }}
            >
              {source.text}
            </a>
          </div>
        )}
      </Card.Header>
      <Card.Body>
        <Card.Title style={{ marginBottom: "20px" }}>{title}</Card.Title>
        {descriptions &&
          descriptions.map((desc, index) => (
            <Card.Text key={index}>{desc}</Card.Text>
          ))}
        {listItems && (
          <div className="card">
            <ul className="list-group list-group-flush">
              {listItems.map((item, index) => (
                <li className="list-group-item" key={index}>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}
        {buttons && (
          <div>
            {buttons.map((button, index) => (
              <Button
                key={index}
                style={{
                  backgroundColor: "#f25286",
                  borderStyle: "none",
                  margin: "10px",
                }}
                variant="primary"
                onClick={() => window.open(button.link, "_blank")}
              >
                {button.text}
              </Button>
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default PortfolioCard;
